import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MuiTooltip from "@mui/material/Tooltip";
import { useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { formatRevenueExpenseCategory } from "layouts/receitaXdespesa/components/receitasxdespesas/revenueExpenseCategories";
import SectionDescriptionHint from "layouts/dashboard/components/SectionDescriptionHint";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});
const HIDDEN_CURRENCY_LABEL = "R$ ****";
const DEFAULT_VISIBLE_CATEGORIES = 5;

const hiddenProgressPalette = {
  fill: "rgba(148, 163, 184, 0.85)",
  soft: "rgba(148, 163, 184, 0.14)",
  border: "rgba(148, 163, 184, 0.24)",
};

const getProgressPalette = (progress) => {
  if (progress >= 100) {
    return {
      fill: "#ef5350",
      soft: "rgba(239, 83, 80, 0.14)",
      border: "rgba(239, 83, 80, 0.24)",
    };
  }

  if (progress >= 75) {
    return {
      fill: "#fb8c00",
      soft: "rgba(251, 140, 0, 0.14)",
      border: "rgba(251, 140, 0, 0.24)",
    };
  }

  if (progress >= 40) {
    return {
      fill: "#1a73e8",
      soft: "rgba(26, 115, 232, 0.12)",
      border: "rgba(26, 115, 232, 0.22)",
    };
  }

  return {
    fill: "#4caf50",
    soft: "rgba(76, 175, 80, 0.14)",
    border: "rgba(76, 175, 80, 0.24)",
  };
};

function ExpenseCategoryProgressCard({
  title,
  description,
  date,
  items,
  toolTipMessage,
  darkMode,
  mobileIcon,
  mobileCollapseEnabled,
  mobileExpanded,
  onToggleMobileCollapse,
  valuesHidden,
}) {
  const shouldShowContent = !mobileCollapseEnabled || mobileExpanded;
  const [showAllCategories, setShowAllCategories] = useState(false);
  const displayCurrency = (value) =>
    valuesHidden ? HIDDEN_CURRENCY_LABEL : currencyFormatter.format(value);
  const visibleItems = showAllCategories ? items : items.slice(0, DEFAULT_VISIBLE_CATEGORIES);
  const hiddenCategoriesCount = Math.max(items.length - DEFAULT_VISIBLE_CATEGORIES, 0);
  const overLimitCount = items.filter((item) => {
    const numericValue = Number(item.value ?? 0);
    const configuredLimitValue = Number(item.configuredLimitValue ?? 0);

    return Boolean(item.hasConfiguredLimit) && configuredLimitValue > 0 && numericValue >= configuredLimitValue;
  }).length;
  const summaryText =
    items.length > 0
      ? `${items.length} categoria${items.length > 1 ? "s" : ""} com despesa${overLimitCount > 0 ? ` • ${overLimitCount} acima do limite` : ""}`
      : "Nenhuma categoria com despesa no mês.";

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox
        pt={2}
        px={2}
        pb={mobileCollapseEnabled && !mobileExpanded ? 2 : 0}
        lineHeight={1.25}
        onClick={mobileCollapseEnabled ? onToggleMobileCollapse : undefined}
        sx={mobileCollapseEnabled ? { cursor: "pointer", userSelect: "none" } : undefined}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <MDBox sx={{ flex: 1, minWidth: 0 }}>
            <MDBox display="flex" alignItems="flex-start" gap={mobileCollapseEnabled ? 1 : 1.25}>
              <MDBox
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(26, 115, 232, 0.12)",
                  color: ({ palette: { info } }) => info.main,
                  flexShrink: 0,
                }}
              >
                <Icon fontSize="small">{mobileIcon}</Icon>
              </MDBox>
              <MDBox>
                <MDBox display="flex" alignItems="center" gap={0.75}>
                  <MDTypography
                    variant="h6"
                    fontWeight="medium"
                    sx={{ color: darkMode ? "#f8fafc" : undefined }}
                  >
                    {title}
                  </MDTypography>
                  <SectionDescriptionHint
                    description={description}
                    isMobile={mobileCollapseEnabled}
                    darkMode={darkMode}
                  />
                </MDBox>
                <MDTypography
                  variant="button"
                  color="text"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined,
                  }}
                >
                  {summaryText}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
          {mobileCollapseEnabled && (
            <Icon
              sx={{
                fontWeight: "bold",
                color: darkMode ? "rgba(226, 232, 240, 0.9)" : ({ palette: { info } }) => info.main,
                mt: 0.15,
              }}
            >
              {mobileExpanded ? "expand_less" : "expand_more"}
            </Icon>
          )}
        </MDBox>
      </MDBox>

      <Collapse in={shouldShowContent} timeout="auto" unmountOnExit>
        <MDBox px={2} pb={2} pt={3}>
          <MDBox>
            {/* <MDBox
              px={2}
              py={1.25}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
                gap: 2,
                borderBottom: `1px solid ${darkMode ? "rgba(148, 163, 184, 0.18)" : "rgba(52, 71, 103, 0.08)"}`,
                backgroundColor: darkMode ? "rgba(30, 41, 59, 0.92)" : "#f8f9fa",
              }}
            >
              <MDTypography
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                sx={{ color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined }}
              >
                Categoria
              </MDTypography>
              <MDTypography
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                sx={{ color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined }}
              >
                Progresso contra o limite configurado
              </MDTypography>
            </MDBox> */}

            {items.length > 0 ? (
              visibleItems.map((item, index) => {
                const numericValue = Number(item.value ?? 0);
                const configuredLimitValue = Number(item.configuredLimitValue ?? 0);
                const hasConfiguredLimit = Boolean(item.hasConfiguredLimit) && configuredLimitValue > 0;
                const usageProgress = hasConfiguredLimit ? Math.min((numericValue / configuredLimitValue) * 100, 100) : 0;
                const displayProgress = valuesHidden ? 0 : usageProgress;
                const badgePosition = valuesHidden
                  ? "50%"
                  : `clamp(52px, ${displayProgress}%, calc(100% - 52px))`;
                const progressPalette = valuesHidden
                  ? hiddenProgressPalette
                  : getProgressPalette(usageProgress);

                return (
                  <MDBox
                    key={item.category}
                    px={2}
                    py={1.5}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
                      gap: 2,
                      alignItems: { xs: "center", md: "stretch" },
                      borderBottom:
                        index === visibleItems.length - 1 && (showAllCategories || hiddenCategoriesCount === 0)
                          ? "none"
                          : `1px solid ${darkMode ? "rgba(148, 163, 184, 0.14)" : "rgba(52, 71, 103, 0.08)"}`,
                    }}
                  >
                    <MDBox
                      sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "flex-end",
                        minWidth: 0,
                        pb: 1,
                      }}
                    >
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        sx={{
                          color: darkMode ? "#e2e8f0" : undefined,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatRevenueExpenseCategory(item.category)}
                      </MDTypography>
                    </MDBox>

                    <MDBox
                      sx={{
                        position: "relative",
                        minHeight: hasConfiguredLimit ? 80 : 52,
                      }}
                    >
                      {hasConfiguredLimit ? (
                        <>
                          <MDBox
                            display="flex"

                            alignItems="center"
                            gap={1}
                            sx={{
                              mb: 0.75,
                              justifyContent: { xs: "space-between", md: "flex-end" }
                            }}
                          >
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              sx={{
                                display: { xs: "block", md: "none" },
                                color: darkMode ? "#e2e8f0" : undefined,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                              }}
                            >
                              {formatRevenueExpenseCategory(item.category)}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: darkMode ? "rgba(226, 232, 240, 0.72)" : "rgba(52, 71, 103, 0.72)",
                                whiteSpace: "nowrap",
                                marginLeft: { xs: 0, md: "auto" },
                              }}
                            >
                              Limite: {displayCurrency(configuredLimitValue)}
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            sx={{
                              position: "absolute",
                              top: 30,
                              height: 27,
                              left: badgePosition,
                              transform: "translateX(-50%)",
                              px: 1.5,
                              py: 0.65,
                              borderRadius: 999,
                              backgroundColor: progressPalette.soft,
                              border: `1px solid ${progressPalette.border}`,
                              boxShadow: darkMode ? "0 10px 24px rgba(15, 23, 42, 0.28)" : "0 10px 24px rgba(15, 23, 42, 0.08)",
                              zIndex: 2,
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ color: progressPalette.fill, whiteSpace: "nowrap", top: -11, position: "relative" }}
                            >
                              {displayCurrency(numericValue)}
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            sx={{
                              position: "absolute",
                              top: 56,
                              left: badgePosition,
                              transform: "translateX(-50%)",
                              width: 2,
                              height: 9,
                              borderRadius: 999,
                              backgroundColor: progressPalette.border,
                              zIndex: 1,
                            }}
                          />
                          <MDBox
                            sx={{
                              position: "absolute",
                              inset: "auto 0 8px 0",
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: darkMode ? "rgba(148, 163, 184, 0.22)" : "#e9ecef",
                            }}
                          />
                          <MDBox
                            sx={{
                              position: "absolute",
                              inset: "auto auto 8px 0",
                              width: `${displayProgress}%`,
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: progressPalette.fill,
                              minWidth: displayProgress > 0 ? 10 : 0,
                            }}
                          />
                        </>
                      ) : (
                        <MDBox
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="medium"
                            sx={{
                              display: { xs: "block", md: "none" },
                              color: darkMode ? "#e2e8f0" : undefined,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatRevenueExpenseCategory(item.category)}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: darkMode ? "#f8fafc" : "#344767" }}
                          >
                            {displayCurrency(numericValue)}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{ color: darkMode ? "rgba(226, 232, 240, 0.72)" : "rgba(52, 71, 103, 0.72)" }}
                          >
                            Sem limite configurado para esta categoria. Defina o valor em Receita x Despesa.
                          </MDTypography>
                          <MDBox
                            sx={{
                              height: 10,
                              borderRadius: 999,
                              border: `1px dashed ${darkMode ? "rgba(148, 163, 184, 0.35)" : "rgba(52, 71, 103, 0.18)"}`,
                              backgroundColor: darkMode ? "rgba(15, 23, 42, 0.35)" : "rgba(233, 236, 239, 0.5)",
                            }}
                          />
                        </MDBox>
                      )}
                    </MDBox>
                  </MDBox>
                );
              })
            ) : (
              <MDBox display="flex" alignItems="center" justifyContent="center" px={2} py={4}>
                <MDTypography
                  variant="button"
                  fontWeight="light"
                  sx={{ color: darkMode ? "rgba(226, 232, 240, 0.78)" : undefined }}
                >
                  Nenhuma despesa encontrada no mês atual.
                </MDTypography>
              </MDBox>
            )}

            {hiddenCategoriesCount > 0 && (
              <MDBox
                px={2}
                pt={1}
                pb={0.5}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  borderTop:
                    showAllCategories
                      ? `1px solid ${darkMode ? "rgba(148, 163, 184, 0.14)" : "rgba(52, 71, 103, 0.08)"}`
                      : "none",
                }}
              >
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => setShowAllCategories((previousState) => !previousState)}
                  sx={{ gap: 0.5 }}
                >
                  <Icon fontSize="small">{showAllCategories ? "expand_less" : "expand_more"}</Icon>
                  {showAllCategories ? "Ver menos" : `Ver todas as ${items.length} categorias`}
                </MDButton>
              </MDBox>
            )}
          </MDBox>

          <MDBox px={2} pb={2}>
            <Divider sx={{ borderColor: darkMode ? "rgba(148, 163, 184, 0.18)" : undefined }} />
            <MDBox display="flex" alignItems="center" pt={2}>
              <MDTypography
                variant="button"
                lineHeight={1}
                sx={{
                  mt: 0.15,
                  mr: 0.5,
                  color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined,
                }}
              >
                {toolTipMessage && (
                  <MuiTooltip title={toolTipMessage}>
                    <Icon>help_outline</Icon>
                  </MuiTooltip>
                )}
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="light"
                sx={{ color: darkMode ? "rgba(226, 232, 240, 0.72)" : undefined }}
              >
                {date}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Collapse>
    </Card>
  );
}

ExpenseCategoryProgressCard.defaultProps = {
  description: "",
  date: "",
  items: [],
  toolTipMessage: "",
  darkMode: false,
  mobileIcon: "health_and_safety",
  mobileCollapseEnabled: false,
  mobileExpanded: true,
  onToggleMobileCollapse: undefined,
  valuesHidden: false,
};

ExpenseCategoryProgressCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  date: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      configuredLimitValue: PropTypes.number,
      hasConfiguredLimit: PropTypes.bool,
    })
  ),
  toolTipMessage: PropTypes.string,
  darkMode: PropTypes.bool,
  mobileIcon: PropTypes.string,
  mobileCollapseEnabled: PropTypes.bool,
  mobileExpanded: PropTypes.bool,
  onToggleMobileCollapse: PropTypes.func,
  valuesHidden: PropTypes.bool,
};

export default ExpenseCategoryProgressCard;
